/**
 * Mutation hook for renaming a location tag
 *
 * @see GitLab #999 — Support for adding location tags
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../../services/api';
import {
  getOfflineLocationTagList,
  isDuplicateLocationTagName,
  mirrorLocationTagsToOfflineCache,
  runSerializedLocationTagOfflineMutation,
} from '../../services/locationTagOfflineSync';
import { networkService } from '../../services/network';
import { offlineCache } from '../../services/offlineCache';
import { locationTagsKeys } from '../queries/useLocationTags';

import type {
  LocationTag,
  LocationTagResponse,
  UpdateLocationTagInput,
} from '../../types/locationTag';

interface UpdateLocationTagParams {
  requestId: string;
  tagId: number;
  data: UpdateLocationTagInput;
}

export const useUpdateLocationTag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    networkMode: 'always',
    mutationFn: async ({
      requestId,
      tagId,
      data,
    }: UpdateLocationTagParams): Promise<LocationTag> => {
      const trimmedName = data.name.trim();
      if (!trimmedName) {
        throw new Error('Location tag name is required');
      }

      if (!networkService.isConnected()) {
        return runSerializedLocationTagOfflineMutation(async () => {
          const existingTags = await getOfflineLocationTagList(requestId);
          if (!existingTags.some(tag => tag.id === tagId)) {
            throw new Error('Location tag not found');
          }
          if (isDuplicateLocationTagName(existingTags, trimmedName, tagId)) {
            throw new Error(
              `Location tag name "${trimmedName}" is already used in this request. Tag names must be unique.`,
            );
          }

          const mergedIntoCreate =
            tagId < 0 &&
            (await offlineCache.renamePendingLocationTagCreate(
              requestId,
              tagId,
              trimmedName,
            ));
          if (!mergedIntoCreate) {
            await offlineCache.addLocationTagToOfflineQueue(
              requestId,
              'update',
              { name: trimmedName },
              tagId,
            );
          }

          const updatedTags = existingTags.map(tag =>
            tag.id === tagId ? { ...tag, name: trimmedName } : tag,
          );
          await mirrorLocationTagsToOfflineCache(requestId, updatedTags);

          const updatedTag = updatedTags.find(tag => tag.id === tagId);
          if (!updatedTag) {
            throw new Error('Location tag not found');
          }
          return updatedTag;
        });
      }

      const response = await api.patch<LocationTagResponse>(
        `/requests/${requestId}/location-tags/${tagId}`,
        { name: trimmedName },
      );
      const cachedTags = await offlineCache.getCachedLocationTags(requestId);
      await mirrorLocationTagsToOfflineCache(
        requestId,
        cachedTags.map(tag => (tag.id === tagId ? response.locationTag : tag)),
      );
      return response.locationTag;
    },
    onSuccess: (_tag, { requestId }) => {
      queryClient.invalidateQueries({
        queryKey: locationTagsKeys.list(requestId),
      });
    },
    meta: {
      errorMessage: 'Failed to rename location tag. Please try again.',
    },
  });
};
